import React from "react";
import { Link } from "react-router-dom";
import { Film, Music2, MessageSquare, Compass, ArrowUpRight } from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#f5f5f5] grain">
      {/* Top nav */}
      <header className="flex items-center justify-between px-6 sm:px-10 py-5 border-b border-[#2a2a2a]">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 flex flex-col overflow-hidden shadow-lg">
            <div className="flex-1 bg-[#00209F]" />
            <div className="flex-1 bg-[#D21034]" />
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-display font-semibold tracking-tight text-lg">Rasin</span>
            <span className="text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3] mt-1">Haiti's Stage · 🇭🇹</span>
          </div>
        </div>
        <nav className="flex items-center gap-2">
          <Link to="/login" data-testid="nav-login" className="px-4 py-2 text-xs uppercase tracking-[0.2em] text-[#a3a3a3] hover:text-[#f5f5f5] transition-colors">
            Sign in
          </Link>
          <Link to="/register" data-testid="nav-register" className="px-5 py-2 text-xs uppercase tracking-[0.2em] bg-[#ff4500] hover:bg-[#ff5722] text-white transition-colors">
            Join free
          </Link>
        </nav>
      </header>

      {/* Hero */}
      <section className="px-6 sm:px-10 pt-20 pb-24 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8 reveal">
            <div className="flex items-center gap-3 mb-6">
              <div className="text-[10px] uppercase tracking-[0.35em] text-[#ff4500] font-mono">
                ── 🇭🇹 1804 · Ayiti devan
              </div>
              <div className="flex h-3 w-10 overflow-hidden">
                <div className="flex-1 bg-[#00209F]" />
                <div className="flex-1 bg-[#D21034]" />
              </div>
            </div>
            <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl leading-[0.9] tracking-tighter font-medium">
              The stage <br />
              for <span className="text-[#ff4500]">Haiti</span> <br />
              and its diaspora.
            </h1>
            <p className="mt-8 text-[#a3a3a3] text-base sm:text-lg max-w-xl leading-relaxed">
              Rasin is the home of Haitian culture, music, video, food, history and art —
              a single platform for the 4 million of us abroad and the 11 million at home
              to show the world what Ayiti really is. Upload. Stream. Talk. Promote.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/register"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#ff4500] hover:bg-[#ff5722] text-white text-sm font-medium transition-colors active:scale-[0.98]"
              >
                Create your profile <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link
                to="/login"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#1a1a1a] border border-[#2a2a2a] hover:bg-[#2a2a2a] text-sm transition-colors"
              >
                Sign in
              </Link>
            </div>
          </div>
          <div className="lg:col-span-4 reveal reveal-2">
            <div className="border border-[#2a2a2a] bg-[#141414] p-6">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#a3a3a3] font-mono">// Sa w jwenn la</div>
              <ul className="mt-5 space-y-4 text-sm">
                <li className="flex items-start gap-3"><span className="text-[#ff4500] font-mono">01</span><span>Upload your videos (MP4) — auto-generated thumbnails</span></li>
                <li className="flex items-start gap-3"><span className="text-[#ff4500] font-mono">02</span><span>Host your music (MP3) — playlists & sticky player</span></li>
                <li className="flex items-start gap-3"><span className="text-[#ff4500] font-mono">03</span><span>Direct messages — live chat over WebSocket</span></li>
                <li className="flex items-start gap-3"><span className="text-[#ff4500] font-mono">04</span><span><span className="text-[#00209F] font-bold">Esplore</span> <span className="text-[#D21034] font-bold">Ayiti</span> — history, geography, food, art, music</span></li>
                <li className="flex items-start gap-3"><span className="text-[#ff4500] font-mono">05</span><span>Promote — shareable link that unfurls on WhatsApp/IG/X</span></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Feature quad */}
      <section className="px-6 sm:px-10 pb-24 max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: Compass, title: "Esplore Ayiti", body: "Read about the 1804 Revolution, the 10 departments, soup joumou (UNESCO heritage), Carnival in Jacmel and more.", tag: "🇭🇹", accent: "#00209F" },
            { icon: Film, title: "Video", body: "Drop your MP4. Auto-thumbnail. Watch in a focused dark player. Share via link with auto-preview.", tag: "MP4", accent: "#ff4500" },
            { icon: Music2, title: "Music", body: "Kompa, Rasin, Rara, Twoubadou, Rabòday — your tracks, your playlists, sticky now-playing.", tag: "MP3", accent: "#D21034" },
            { icon: MessageSquare, title: "Chat", body: "Direct messages and group rooms. Real-time. Connect with creators across the diaspora.", tag: "LIVE", accent: "#F2B400" },
          ].map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className={`bg-[#141414] border border-[#2a2a2a] hover:border-[#ff4500]/40 transition-colors p-6 reveal reveal-${i + 1}`}>
                <div className="flex items-center justify-between">
                  <Icon className="w-6 h-6" style={{ color: f.accent }} />
                  <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#a3a3a3]">{f.tag}</span>
                </div>
                <h3 className="font-display text-2xl mt-6 tracking-tight">{f.title}</h3>
                <p className="text-sm text-[#a3a3a3] mt-3 leading-relaxed">{f.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Manifesto */}
      <section className="px-6 sm:px-10 py-20 border-t border-[#2a2a2a] bg-[#141414]">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex justify-center mb-4">
            <div className="flex h-2 w-16 overflow-hidden">
              <div className="flex-1 bg-[#00209F]" />
              <div className="flex-1 bg-[#D21034]" />
            </div>
          </div>
          <h2 className="font-display text-3xl sm:text-5xl tracking-tighter">L'union fait la force.</h2>
          <p className="text-[#a3a3a3] mt-4 text-sm leading-relaxed">
            From the Bois Caïman ceremony of 1791 to the diaspora today — when Haitians unite,
            we win. Rasin is built so we can tell our own story, in our own voice. Free, for all of us.
          </p>
          <Link to="/register" className="inline-flex items-center gap-2 mt-8 px-6 py-3 bg-[#ff4500] hover:bg-[#ff5722] text-white text-sm">
            Vin sou Rasin <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <footer className="px-6 sm:px-10 py-8 border-t border-[#2a2a2a] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-[#a3a3a3]">
        <div className="font-mono uppercase tracking-[0.25em]">© Rasin · Ayiti devan · 2026</div>
        <div className="font-mono uppercase tracking-[0.25em]">L'union fait la force 🇭🇹</div>
      </footer>
    </div>
  );
}
